/**
 * The ranking criteria, shared by the server and the browser.
 *
 * WHY THIS IS NOT SERVER-ONLY
 * ---------------------------
 * This list is static — it lives in code and changes only when a criterion is
 * added. It used to reach the browser inside every leaderboard response, which
 * meant the criteria picker rendered EMPTY until that request came back: on a
 * cold load the API took ~8 seconds, so the filters sat blank the whole time
 * for data the bundle could have carried all along.
 *
 * Importing it directly lets the picker paint on first render, with no network
 * involved. The API still returns the same list so its responses stay
 * self-describing for any other consumer.
 */

export type BoardId =
  | 'cheapest_per_100g'
  | 'cheapest_per_100ml'
  | 'biggest_discount'
  | 'largest_package'
  | 'cheapest_outright'
  | 'protein_per_dollar'
  | 'cheapest_per_100kcal'
  | 'protein_per_100kcal'
  | 'carbs_per_100kcal'
  | 'fat_per_100kcal'
  | 'highest_protein'
  | 'highest_carbs'
  | 'highest_fat';

export interface BoardDef {
  id: BoardId;
  label: string;
  description: string;
  formula: string;
  needsNutrition: boolean;
  group: 'value' | 'density' | 'amount';
}

export const BOARDS: BoardDef[] = [
  {
    id: 'cheapest_per_100g',
    label: 'Cheapest per 100 g',
    description: 'Most food by weight for your money.',
    formula: 'price ÷ package weight × 100',
    needsNutrition: false,
    group: 'value',
  },
  {
    id: 'cheapest_per_100ml',
    label: 'Cheapest per 100 ml',
    description: 'Most drink by volume for your money.',
    formula: 'price ÷ package volume × 100',
    needsNutrition: false,
    group: 'value',
  },
  {
    id: 'biggest_discount',
    label: 'Biggest discounts',
    description: 'Largest price reductions currently on offer.',
    formula: '(original price − sale price) ÷ original price × 100',
    needsNutrition: false,
    group: 'value',
  },
  {
    id: 'largest_package',
    label: 'Largest packages',
    description: 'Biggest pack sizes, for bulk buying.',
    formula: 'net package quantity',
    needsNutrition: false,
    group: 'amount',
  },
  {
    id: 'cheapest_outright',
    label: 'Lowest price',
    description: 'Cheapest sticker price — not the same as best value.',
    formula: 'listed price',
    needsNutrition: false,
    group: 'value',
  },
  {
    id: 'protein_per_dollar',
    label: 'Most protein per dollar',
    description: 'Cheapest protein by weight. Not a measure of protein quality.',
    formula: 'package protein (g) ÷ price',
    needsNutrition: true,
    group: 'value',
  },
  {
    id: 'cheapest_per_100kcal',
    label: 'Cheapest per 100 kcal',
    description: 'Only ranks products whose nutrition has been resolved.',
    formula: 'price ÷ package energy (kcal) × 100',
    needsNutrition: true,
    group: 'value',
  },
  // --- Macronutrient density: how much of a macro per 100 kcal -------------
  {
    id: 'protein_per_100kcal',
    label: 'Most protein per 100 kcal',
    description: 'Most protein for the fewest calories.',
    formula: 'protein (g) ÷ energy (kcal) × 100',
    needsNutrition: true,
    group: 'density',
  },
  {
    id: 'carbs_per_100kcal',
    label: 'Most carbs per 100 kcal',
    description: 'Most carbohydrate for the fewest calories.',
    formula: 'carbohydrate (g) ÷ energy (kcal) × 100',
    needsNutrition: true,
    group: 'density',
  },
  {
    id: 'fat_per_100kcal',
    label: 'Most fat per 100 kcal',
    description: 'Most fat for the fewest calories.',
    formula: 'fat (g) ÷ energy (kcal) × 100',
    needsNutrition: true,
    group: 'density',
  },
  // --- Raw amount per 100 g / 100 ml ---------------------------------------
  {
    id: 'highest_protein',
    label: 'Highest protein',
    description: 'Most protein per 100 g or 100 ml, regardless of price.',
    formula: 'protein (g) per 100 g / 100 ml',
    needsNutrition: true,
    group: 'amount',
  },
  {
    id: 'highest_carbs',
    label: 'Highest carbs',
    description: 'Most carbohydrate per 100 g or 100 ml.',
    formula: 'carbohydrate (g) per 100 g / 100 ml',
    needsNutrition: true,
    group: 'amount',
  },
  {
    id: 'highest_fat',
    label: 'Highest fat',
    description: 'Most fat per 100 g or 100 ml.',
    formula: 'fat (g) per 100 g / 100 ml',
    needsNutrition: true,
    group: 'amount',
  },
];

export function boardById(id: string): BoardDef | null {
  return BOARDS.find((b) => b.id === id) ?? null;
}
